
import { Component, Vue } from 'vue-property-decorator';
import { Transaction } from '../../models/Transaction';
import { ErrorHandler } from '../../helpers/errorHandler';
import { PricingPlans, IPricigPlan } from '../../helpers/pricingPlan';
import i18n from '../../plugins/i18n';
import { useBraintree } from '../../composables/useBraintree';

@Component({})
export default class ActivateSubscription2 extends Vue {
    private show: boolean = true;
    private loading: boolean = true;
    private showChangeCardForm: boolean = false;
    private actionLoading: boolean = false;
    private paymentStatus: string = '';
    private token: string = '';
    private numberToken = '';

    private errorCreditCardNumber: boolean = false;
    private errorCardholderName: boolean = false;
    private errorExpirationDate: boolean = false;
    private errorCvv: boolean = false;
    private errorGeneral: boolean = false;
    private errorMessageCreditCardNumber: string = '';
    private errorMessageCardholderName: string = '';
    private errorMessageExpirationDate: string = '';
    private errorMessageCvv: string = '';
    private errorMessageGeneral: string = '';

    private rules: any = {
        required: (value) => !!value || i18n.t('validation.required'),
    };

    private clientInstance: any = null;
    private threeDSecureInstance: any = null;
    private braintree = useBraintree();

    private pricingPlans = new PricingPlans();
    private currencyPlan: IPricigPlan = {
        name:  this.pricingPlans.Basic.name,
        type: this.pricingPlans.Basic.type,
        price: this.pricingPlans.Basic.price,
        currency: this.pricingPlans.Basic.currency,
        limit: this.pricingPlans.Basic.limit
    };

    private transaction: Transaction = {
        cardholderName: '',
        pricingPlan: this.pricingPlans.Basic.type.toLowerCase(),
        paymentMethodNonce: '',
        creditCardNumber: '',
        expirationDate: '',
        cvv: '',
        newCard: true,
        imageUrl: '',
        trialDuration: 0
    };

    private userEmail: string = '';

    private logout() {
        sessionStorage.clear();
        this.$router.push({ name: 'login' });
    }

    private toPage() {
        this.$router.push({ name: 'campaignlist' });
    }

    private async created() {
        try {
            this.loading = true;

            try {
                // @ts-ignore
                const response = await this.axios.get(`/api/braintree/getdefaultpaymentMethod`);
                const data = response.data.data;

                if (data != null && data.maskedNumber != null) {
                    this.transaction.creditCardNumber = data.maskedNumber;
                    this.transaction.expirationDate = data.expirationDate;
                    this.transaction.imageUrl = data.imageUrl;
                    this.numberToken = data.token;
                    this.transaction.newCard = false;
                } else {
                    this.showChangeCardForm = true;
                }

                this.transaction.trialDuration = data?.trialDuration ?? 0;
                this.userEmail = data?.email || '';

                if (data?.currency === 'EUR') {
                    this.currencyPlan = this.pricingPlans.Basic;
                } else if (data?.currency === 'USD') {
                    this.currencyPlan = this.pricingPlans.BasicUSD;
                }

                this.transaction.pricingPlan = this.currencyPlan.type;
            } catch (e: any) {
                // Payment method not found — show full card form
                this.showChangeCardForm = true;
            }

            await this.generateToken();
        } catch (error: any) {
            new ErrorHandler().Server(error);
        } finally {
            this.loading = false;
        }
    }

    private async generateToken() {
        try {
            // @ts-ignore
            const response = await this.axios.get(`/api/braintree/GenerateToken`);
            this.token = response.data.data;
            await this.createBraintreeInstance();
        } catch (error: any) {
            new ErrorHandler().Server(error);
        }
    }

    private changeCard() {
        this.transaction.creditCardNumber = '';
        this.transaction.expirationDate = '';
        this.transaction.imageUrl = '';
        this.transaction.cvv = '';
        this.transaction.newCard = true;
        this.showChangeCardForm = true;
    }

    private async createBraintreeInstance() {
        try {
            this.clientInstance = await this.braintree.initClient(this.token);
            this.transaction.deviceData = await this.braintree.initDataCollector(this.clientInstance);
            this.threeDSecureInstance = await this.braintree.initThreeDSecure(this.clientInstance);
            this.loading = false;
        } catch (error: any) {
            alert(error);
        }
    }

    private async createPaymentRequest() {
        this.resetErrors();

        // @ts-ignore
        if (this.$refs.form && this.$refs.form.validate() === false) {
            return;
        }

        if (this.showChangeCardForm && !this.transaction.cardholderName) {
            this.errorCardholderName = true;
            this.errorMessageCardholderName = String(i18n.t('validation.required'));
            return;
        }

        this.actionLoading = true;

        try {
            let expirationDate = this.transaction.expirationDate;
            if (expirationDate && expirationDate.indexOf('/') === -1) {
                expirationDate = expirationDate.substring(0, 2) + '/' + expirationDate.substring(2, 4);
            }

            let cardNumber = this.transaction.creditCardNumber;
            if (this.transaction.newCard === false) {
                const token = this.numberToken.replace(/\$/g, '');
                cardNumber = this.transaction.creditCardNumber.replace('******', token);
            }

            const tokenized = await this.braintree.tokenizeRawCard(this.clientInstance, {
                number: cardNumber,
                expirationDate,
                cvv: this.transaction.cvv,
                cardholderName: this.transaction.cardholderName,
            });

            const amount = String(this.currencyPlan.price);
            const verifyResult = await this.braintree.verify3DSecure(this.threeDSecureInstance, {
                nonce: tokenized.nonce,
                bin: tokenized.bin,
                amount,
                email: this.userEmail,
                cardholderName: this.transaction.cardholderName,
                challengeRequested: true,
                collectDeviceData: true,
            });

            const evaluation = this.braintree.evaluate3DSResult(verifyResult);
            if (!evaluation.accepted) {
                this.errorGeneral = true;
                this.errorMessageGeneral = 'Your bank could not verify your identity. Please try again or use a different card.';
                this.actionLoading = false;
                return;
            }

            await this.createPayment(verifyResult.nonce, verifyResult.threeDSecureAuthenticationId);
        } catch (error: any) {
            this.actionLoading = false;
            if (error && error.details && error.details.originalError && error.details.originalError.fieldErrors) {
                error.details.originalError.fieldErrors.forEach((err: any) => {
                    err.fieldErrors.forEach((fieldError: any) => {
                        switch (fieldError.field) {
                            case 'number':
                                this.errorCreditCardNumber = true;
                                this.errorMessageCreditCardNumber = fieldError.message;
                                break;
                            case 'expirationMonth':
                                this.errorExpirationDate = true;
                                this.errorMessageExpirationDate = fieldError.message;
                                break;
                            case 'cvv':
                                this.errorCvv = true;
                                this.errorMessageCvv = fieldError.message;
                                break;
                        }
                    });
                });
            } else {
                this.errorGeneral = true;
                this.errorMessageGeneral = error && error.message ? error.message : 'An error occurred. Please try again.';
            }
        }
    }

    private async createPayment(nonce: string, threeDSecureAuthenticationId: string | null) {
        const payment: object = {
            paymentMethodNonce: nonce,
            threeDSecureAuthenticationId,
            newCard: this.transaction.newCard,
            pricingPlan: this.transaction.pricingPlan,
            cardholderName: this.transaction.cardholderName,
            creditCardNumber: this.transaction.creditCardNumber,
            deviceData: this.transaction.deviceData,
        };

        try {
            // @ts-ignore
            await this.axios.post<Transaction>(`/api/braintree/createpayment`, payment);

            if (this.clientInstance) {
                this.clientInstance.teardown();
            }

            setTimeout(() => {
                this.actionLoading = false;
                this.toPage();
            }, 3000);
        } catch (error: any) {
            new ErrorHandler().Server(error);
            this.errorCreditCardNumber = true;
            this.errorMessageCreditCardNumber = new ErrorHandler().errorMessage;
            this.actionLoading = false;
        }
    }

    private resetErrors() {
        this.errorCreditCardNumber = false;
        this.errorCardholderName = false;
        this.errorExpirationDate = false;
        this.errorCvv = false;
        this.errorGeneral = false;
        this.errorMessageCreditCardNumber = '';
        this.errorMessageCardholderName = '';
        this.errorMessageExpirationDate = '';
        this.errorMessageCvv = '';
        this.errorMessageGeneral = '';
    }
}

